import { useEffect, useState, useRef, useCallback } from "react";
import HearingTestButton from "../../../components/hearingTestButton/hearingTestButton";
import PillTitle from "../../../components/pillTitle/pillTitle";
import { useI18n } from "../../../utils/translate/i18n/useI18n";
import type { Ear } from "../../../type/tonalTest";
import type { VerbalPanelState, VerbalWordRecord, WRSPanelProps } from "../../../type/verbalTest";
import { hasSpeechRecognitionSupport } from "../../../type/verbalTest";
import { speakToEar } from "../../../utils/speakToEar";
import wordsDb from "../../../assets/words/words_pl.json";
import { WRS_START_DB, WRS_WORDS_PER_LEVEL, WRS_RESPONSE_TIMEOUT_MS, WRS_EARLY_STOP_AFTER, WRS_WORDS_PER_LEVEL_EXT, WRS_EARLY_STOP_AFTER_EXT, HEADPHONE_VOLUME_COMPENSATION } from "../../../utils/config";

// ── dB scale (0–100, 5 dB steps) ─────────────────────────────────────────────
const dBLevels = Array.from({ length: 21 }, (_, i) => i * 5);
const speechVolume = (dB: number): number => {
  if (dB === 0) return 0.001 * HEADPHONE_VOLUME_COMPENSATION;
  return Math.min(1.0, Math.max(0.001, 0.5 * HEADPHONE_VOLUME_COMPENSATION * Math.pow(10, (dB - 75) / 20)));
};

// WRS_WORDS_PER_LEVEL, WRS_EARLY_STOP_AFTER, WRS_START_DB, WRS_RESPONSE_TIMEOUT_MS come from config.tsx

function getWRSWordPool(ear: Ear): string[] {
  const listIdx = ear === "left" ? 0 : 1;
  const list = wordsDb.wrs_lists[listIdx % wordsDb.wrs_lists.length];
  return [...list.words];
}

function getWRSSentencePool(ear: Ear): string[] {
  const listIdx = ear === "left" ? 0 : 1;
  const list = wordsDb.wrs_sentence_lists[listIdx % wordsDb.wrs_sentence_lists.length];
  return [...list.sentences];
}

// ── Phonetic similarity helpers for Polish monosyllabic words ─────────────────
const POLISH_VOWELS = new Set(["a", "e", "i", "o", "u", "y", "ą", "ę", "ó"]);

function extractRhyme(word: string): string {
  const w = word.toLowerCase();
  for (let i = 0; i < w.length; i++) {
    if (POLISH_VOWELS.has(w[i])) return w.slice(i);
  }
  return w;
}

function extractVowel(word: string): string {
  for (const ch of word.toLowerCase()) {
    if (POLISH_VOWELS.has(ch)) return ch;
  }
  return "";
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function phoneticScore(target: string, candidate: string): number {
  const t = target.toLowerCase();
  const c = candidate.toLowerCase();
  let score = 0;
  const tRhyme = extractRhyme(t);
  const cRhyme = extractRhyme(c);
  if (tRhyme === cRhyme) score += 100;
  else if (tRhyme.startsWith(cRhyme) || cRhyme.startsWith(tRhyme)) score += 50;
  if (extractVowel(t) === extractVowel(c)) score += 30;
  if (t[t.length - 1] === c[c.length - 1]) score += 20;
  if (t[0] === c[0]) score += 10;
  score += Math.max(0, 20 - levenshtein(t, c) * 5);
  return score;
}

// Pool of all WRS words across all lists, used for distractor selection
const allWrsWords: string[] = wordsDb.wrs_lists.flatMap(l => l.words);
const allWrsSentences: string[] = wordsDb.wrs_sentence_lists.flatMap(l => l.sentences);

function buildChoices(target: string): string[] {
  const others = allWrsWords.filter(w => w.toLowerCase() !== target.toLowerCase());
  const scored = others.map(w => ({
    word: w,
    score: phoneticScore(target, w) + Math.random() * 5
  }));
  scored.sort((a, b) => b.score - a.score);
  const distractors = scored.slice(0, 3).map(s => s.word);
  return [...distractors, target].sort(() => Math.random() - 0.5);
}

function buildSentenceChoices(target: string): string[] {
  const others = allWrsSentences.filter(s => s !== target);
  const shuffled = [...others].sort(() => Math.random() - 0.5).slice(0, 3);
  return [...shuffled, target].sort(() => Math.random() - 0.5);
}

// ── Sentence mic-matching helpers ─────────────────────────────────────────────
const SENTENCE_STOP_WORDS = new Set(["w", "z", "na", "do", "po", "ze", "i", "a", "się", "że", "to", "jest", "są", "o", "u", "od"]);

function contentWords(sentence: string): string[] {
  return sentence.toLowerCase()
    .replace(/[.,!?;:]/g, "")
    .split(/\s+/)
    .filter(w => w.length > 2 && !SENTENCE_STOP_WORDS.has(w));
}

function isSentenceMatch(target: string, transcripts: string[]): boolean {
  const tWords = contentWords(target);
  if (tWords.length === 0) return false;
  return transcripts.some(tr => {
    const trNorm = tr.toLowerCase().replace(/[.,!?;:]/g, "");
    const matched = tWords.filter(w => trNorm.split(/\s+/).includes(w));
    return matched.length === tWords.length;
  });
}

function computeMaxWrs(records: VerbalWordRecord[]): number {
  const map = new Map<number, { correct: number; total: number }>();
  for (const r of records) {
    const e = map.get(r.dB) ?? { correct: 0, total: 0 };
    e.total++;
    if (r.heard) e.correct++;
    map.set(r.dB, e);
  }
  let max = 0;
  for (const e of map.values()) {
    const pct = Math.round((e.correct / e.total) * 100);
    if (pct > max) max = pct;
  }
  return max;
}

export default function VerbalWRSPanel({
  ear, useMicrophone, useSentences, extendedWRS, onComplete, onCancel, user
}: WRSPanelProps) {
  const { t } = useI18n();

  const WRS_WORDS = extendedWRS ? WRS_WORDS_PER_LEVEL_EXT : WRS_WORDS_PER_LEVEL;
  const WRS_STOP  = extendedWRS ? WRS_EARLY_STOP_AFTER_EXT : WRS_EARLY_STOP_AFTER;

  // Word pool and per-level word lists
  const wordPool   = useRef(useSentences ? getWRSSentencePool(ear) : getWRSWordPool(ear));
  const levelWords = useRef<Map<number, string[]>>(new Map());
  const getWordsForLevel = (volIdx: number): string[] => {
    if (!levelWords.current.has(volIdx)) {
      const shuffled = [...wordPool.current].sort(() => Math.random() - 0.5);
      levelWords.current.set(volIdx, shuffled.slice(0, WRS_WORDS));
    }
    return levelWords.current.get(volIdx)!;
  };

  const [testPhase, setTestPhase]             = useState<"ascending" | "descending">("ascending");
  const [volIndex, setVolIndex]               = useState(() => {
    const idx = dBLevels.indexOf(WRS_START_DB);
    return idx >= 0 ? idx : 12; // fallback: 60 dB is index 12
  });
  const [wordIndexInLevel, setWordIndexInLevel] = useState(0);
  const [correctInLevel, setCorrectInLevel]   = useState(0);
  const [allWordRecords, setAllWordRecords]   = useState<VerbalWordRecord[]>([]);
  const [state, setState]                     = useState<VerbalPanelState>({ phase: "playing" });
  const [isFinished, setIsFinished]           = useState(false);
  const recognitionRef        = useRef<SpeechRecognition | null>(null);
  const feedbackTimer          = useRef<number | null>(null);
  const responseTimeoutRef     = useRef<number | null>(null);
  const micResultReceivedRef   = useRef(false);
  const cancelSpeech           = useRef<(() => void) | null>(null);
  const [timeLeft, setTimeLeft]             = useState<number | null>(null);
  const countdownIntervalRef               = useRef<number | null>(null);

  const clearResponseTimeout = () => {
    if (responseTimeoutRef.current !== null) {
      window.clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setTimeLeft(null);
  };

  const startCountdown = (ms: number) => {
    const totalSec = Math.ceil(ms / 1000);
    setTimeLeft(totalSec);
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
    }
    countdownIntervalRef.current = window.setInterval(() => {
      setTimeLeft(prev => {
        if (prev === null || prev <= 1) {
          window.clearInterval(countdownIntervalRef.current!);
          countdownIntervalRef.current = null;
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const wrsDb             = dBLevels[Math.min(volIndex, dBLevels.length - 1)];
  const currentLevelWords = getWordsForLevel(volIndex);
  const currentWord       = currentLevelWords[wordIndexInLevel] ?? currentLevelWords[0];
  const canUseMic         = useMicrophone && hasSpeechRecognitionSupport();
  const progressPct       = Math.round((wordIndexInLevel / WRS_WORDS) * 100);

  // ── Play word via TTS routed to the tested ear only ──────────────────────
  const playCurrentWord = useCallback(() => {
    cancelSpeech.current?.();
    cancelSpeech.current = speakToEar(
      currentWord,
      speechVolume(wrsDb),
      ear,
      () => {
        // onEnd
        cancelSpeech.current = null;
        clearResponseTimeout();
        if (canUseMic) {
          setState({ phase: "awaiting_mic" });
          micResultReceivedRef.current = false;
          startMicRecognition();
          responseTimeoutRef.current = window.setTimeout(() => {
            responseTimeoutRef.current = null;
            // Signal that our timeout fired, then stop recognition.
            // rec.onend will see responseTimeoutRef === null and record wrong.
            if (recognitionRef.current) {
              try { recognitionRef.current.stop(); } catch { /* ignore */ }
            } else {
              // Recognition already ended (e.g. restarting) — record directly
              if (!micResultReceivedRef.current) {
                micResultReceivedRef.current = true;
                recordAnswer(false, "");
              }
            }
          }, WRS_RESPONSE_TIMEOUT_MS);
          startCountdown(WRS_RESPONSE_TIMEOUT_MS);
        } else {
          setState({ phase: "awaiting_choice", choices: useSentences ? buildSentenceChoices(currentWord) : buildChoices(currentWord) });
          responseTimeoutRef.current = window.setTimeout(() => {
            responseTimeoutRef.current = null;
            recordAnswer(false, "");
          }, WRS_RESPONSE_TIMEOUT_MS);
          startCountdown(WRS_RESPONSE_TIMEOUT_MS);
        }
      },
      () => {
        // onError
        cancelSpeech.current = null;
        clearResponseTimeout();
        recordAnswer(false, "");
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWord, wrsDb, canUseMic]);

  // ── Microphone recognition ─────────────────────────────────────────────────
  const startMicRecognition = useCallback(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) return;

    const rec = new SpeechRecognitionAPI();
    rec.lang = "pl-PL";
    rec.interimResults = false;
    rec.maxAlternatives = 3;

    rec.onresult = (event: SpeechRecognitionEvent) => {
      micResultReceivedRef.current = true;
      clearResponseTimeout();
      const transcripts = Array.from({ length: event.results[0].length }, (_, i) =>
        event.results[0][i].transcript.trim().toLowerCase()
      );
      const correct = useSentences
        ? isSentenceMatch(currentWord, transcripts)
        : transcripts.some(tr => tr.trim() === currentWord.toLowerCase());
      recordAnswer(correct, transcripts[0] ?? "");
    };

    rec.onerror = (event: Event) => {
      // "no-speech" is the browser's own silence cut-off — not a real error,
      // onend will fire next and handle the restart logic.
      if ((event as SpeechRecognitionEvent & { error: string }).error === "no-speech") return;
      micResultReceivedRef.current = true;
      clearResponseTimeout();
      recordAnswer(false, "");
    };

    rec.onend = () => {
      recognitionRef.current = null;
      if (!micResultReceivedRef.current) {
        // If our overall timeout is still running, the browser silenced us —
        // restart recognition so the patient has the full allocated time.
        if (responseTimeoutRef.current !== null) {
          startMicRecognition();
        } else {
          // Our timeout already fired → record as incorrect
          micResultReceivedRef.current = true;
          recordAnswer(false, "");
        }
      }
    };

    recognitionRef.current = rec;
    try { rec.start(); } catch { /* already started */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWord]);

  // ── Record an answer and advance ──────────────────────────────────────────
  const recordAnswer = useCallback((correct: boolean, response = "") => {
    clearResponseTimeout();
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }

    const record: VerbalWordRecord = {
      word: currentWord,
      dB: wrsDb,
      heard: correct,
      response
    };

    const newRecords         = [...allWordRecords, record];
    const newCorrectInLevel  = correctInLevel + (correct ? 1 : 0);
    const nextWordInLevel    = wordIndexInLevel + 1;

    setAllWordRecords(newRecords);
    setCorrectInLevel(newCorrectInLevel);
    setState({ phase: "feedback", correct, correctWord: currentWord });

    feedbackTimer.current = window.setTimeout(() => {
      // Early stop: after WRS_STOP words with 0 correct → skip level
      if (nextWordInLevel === WRS_STOP && newCorrectInLevel === 0) {
        if (testPhase === "ascending") {
          // Ascending: jump up to next level
          const nextVolIndex = volIndex + 1;
          if (nextVolIndex >= dBLevels.length) {
            setIsFinished(true);
            onComplete(ear, computeMaxWrs(newRecords), newRecords);
          } else {
            setVolIndex(nextVolIndex);
            setWordIndexInLevel(0);
            setCorrectInLevel(0);
            setState({ phase: "playing" });
          }
        } else {
          // Descending: 0% reached → finish
          setIsFinished(true);
          onComplete(ear, computeMaxWrs(newRecords), newRecords);
        }
        return;
      }

      // End of level (all words done)
      if (nextWordInLevel >= WRS_WORDS) {
        if (testPhase === "ascending") {
          if (newCorrectInLevel === WRS_WORDS) {
            // 100% in ascending → switch to descending phase starting at 55 dB
            const descStartIdx = dBLevels.indexOf(55);
            setTestPhase("descending");
            setVolIndex(descStartIdx >= 0 ? descStartIdx : 11);
            setWordIndexInLevel(0);
            setCorrectInLevel(0);
            setState({ phase: "playing" });
          } else {
            // Not 100% → go up
            const nextVolIndex = volIndex + 1;
            if (nextVolIndex >= dBLevels.length) {
              setIsFinished(true);
              onComplete(ear, computeMaxWrs(newRecords), newRecords);
            } else {
              setVolIndex(nextVolIndex);
              setWordIndexInLevel(0);
              setCorrectInLevel(0);
              setState({ phase: "playing" });
            }
          }
        } else {
          // Descending phase
          if (newCorrectInLevel === 0) {
            // 0% → finish
            setIsFinished(true);
            onComplete(ear, computeMaxWrs(newRecords), newRecords);
          } else {
            // Not 0% yet → go down
            const nextVolIndex = volIndex - 1;
            if (nextVolIndex < 0) {
              setIsFinished(true);
              onComplete(ear, computeMaxWrs(newRecords), newRecords);
            } else {
              setVolIndex(nextVolIndex);
              setWordIndexInLevel(0);
              setCorrectInLevel(0);
              setState({ phase: "playing" });
            }
          }
        }
        return;
      }

      // Continue to next word in current level
      setWordIndexInLevel(nextWordInLevel);
      setState({ phase: "playing" });
    }, 1200);
  }, [WRS_STOP, WRS_WORDS, allWordRecords, correctInLevel, currentWord, ear, onComplete, testPhase, volIndex, wordIndexInLevel, wrsDb]);

  // ── Play word when word index or level changes ────────────────────────────
  useEffect(() => {
    if (isFinished || state.phase !== "playing") return;
    playCurrentWord();
    return () => { cancelSpeech.current?.(); cancelSpeech.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordIndexInLevel, volIndex, isFinished]);

  useEffect(() => {
    return () => {
      cancelSpeech.current?.();
      cancelSpeech.current = null;
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
      if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
      clearResponseTimeout();
    };
  }, []);

  const handleChoiceClick = (choice: string) => {
    if (state.phase !== "awaiting_choice") return;
    const correct = useSentences
      ? choice === currentWord
      : choice.toLowerCase() === currentWord.toLowerCase();
    recordAnswer(correct, choice);
  };

  // ⌨️ keyboard shortcut: Esc/N = "Nie słyszę / Nie rozumiem"
  useEffect(() => {
    const canAnswerNo = state.phase === "awaiting_choice" || state.phase === "awaiting_mic";
    if (!canAnswerNo) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape" || e.key === "Escape" || e.key.toLowerCase() === "n") {
        e.preventDefault();
        recordAnswer(false, "");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [state.phase, recordAnswer]);

  // const handleReplayWord = () => {
  //   setState({ phase: "playing" });
  // };

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="verbal-test__panel-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
          <div className="test-panel__summary">
            <PillTitle as="div" variant="blue-darkner" iconClassName="bi bi-list-check" text={t("Test WRS")} />
            <h2 className="mb-1">
              {t("Rozpoznawanie słów")} — {ear === "left" ? t("Lewe ucho") : t("Prawe ucho")}{" "}
              
            </h2>
            <p className="mb-0">
              {canUseMic
                ? t("Powtórz usłyszane słowo do mikrofonu. MÓW GŁOŚNO i WYRAŹNIE !!!")
                : t("Wybierz słowo, które usłyszałeś/aś.")}
            </p>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {user?.admin === true && (
                <>
                  <span className="badge rounded-pill text-bg-light">{currentWord}</span>
                  <span className="badge rounded-pill text-bg-light">{wrsDb} dB</span>
                </>
              )}
              <span className="badge rounded-pill text-bg-light">{wordIndexInLevel + 1}/{WRS_WORDS} {t("słów")}</span>
            </div>
          </div>
          <div className="text-md-end">
            <span className="small text-uppercase">{t("Głośność")}</span>{" "}
            <strong>{wrsDb} dB</strong>
          </div>
        </div>

        <div className="progress-bar mb-3">
          <div style={{ width: `${progressPct}%` }} />
        </div>

        {/* ── State-dependent UI ──────────────────────────────────────────── */}
        {state.phase === "playing" && (
          <div className="verbal-test__word-display">
            <span className="verbal-test__speaking-icon"><i className="bi bi-volume-up-fill" /></span>
            <span className="verbal-test__word-status">{t("Słowo odtwarzane…")}</span>
          </div>
        )}

        {state.phase === "awaiting_mic" && (
          <div className="verbal-test__mic-waiting">
            <div className="verbal-test__mic-animation">
              <i className="bi bi-mic-fill" />
            </div>
            <p className="verbal-test__word-status">{t("Powtórz słowo do mikrofonu…")}</p>
            {timeLeft !== null && (
              <div className="verbal-wrs__countdown">
                <svg className="verbal-wrs__countdown-svg" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.9"
                    fill="none" stroke="#f4c56c" strokeWidth="3"
                    strokeDasharray={`${(timeLeft / Math.ceil(WRS_RESPONSE_TIMEOUT_MS / 1000)) * 100} 100`}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)"
                  />
                </svg>
                <span className="verbal-wrs__countdown-value">{timeLeft}</span>
              </div>
            )}
            {!useSentences && (
            <button
              type="button"
              className="btn btn-sm btn-outline-light btn-switch-to-buttons"
              onClick={() => {
                micResultReceivedRef.current = true;
                clearResponseTimeout();
                if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
                setState({ phase: "awaiting_choice", choices: buildChoices(currentWord) });
                // Start timeout for the choice phase
                responseTimeoutRef.current = window.setTimeout(() => {
                  responseTimeoutRef.current = null;
                  recordAnswer(false, "");
                }, WRS_RESPONSE_TIMEOUT_MS);
                startCountdown(WRS_RESPONSE_TIMEOUT_MS);
              }}
            >
              {t("Użyj przycisków")}
            </button>
            )}
          </div>
        )}

        {state.phase === "awaiting_choice" && (
          <>
            {timeLeft !== null && (
              <div className="verbal-wrs__countdown verbal-wrs__countdown--bar">
                <svg className="verbal-wrs__countdown-svg" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.9"
                    fill="none" stroke="#f4c56c" strokeWidth="3"
                    strokeDasharray={`${(timeLeft / Math.ceil(WRS_RESPONSE_TIMEOUT_MS / 1000)) * 100} 100`}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)"
                  />
                </svg>
                <span className="verbal-wrs__countdown-value">{timeLeft}</span>
              </div>
            )}
            <div className="verbal-wrs__choices">
            {state.choices.map(choice => (
              <button
                key={choice}
                type="button"
                className="verbal-wrs__choice-btn"
                onClick={() => handleChoiceClick(choice)}
              >
                {choice}
              </button>
            ))}
            </div>
          </>
        )}

        {state.phase === "feedback" && (
          <div className={`verbal-wrs__feedback verbal-wrs__feedback--${state.correct ? "correct" : "incorrect"}`}>
            <i className={`bi ${state.correct ? "bi-check-circle-fill" : "bi-x-circle-fill"}`} />
            <span>
              {state.correct
                ? t("Dobrze!")
                : `${t("Słowo to:")} ${state.correctWord}`}
            </span>
          </div>
        )}

        </div>
        {/* ── Bottom actions ──────────────────────────────────────────────── */}
        <div className="d-flex flex-column flex-sm-row justify-content-end gap-3">
          {(state.phase === "awaiting_choice" || state.phase === "awaiting_mic") && (
            <HearingTestButton
              type="button"
              className="btn btn-outline-light btn-lg text-danger flex-grow-1"
              iconClassName="bi bi-ear"
              onClick={() => {
                micResultReceivedRef.current = true;
                clearResponseTimeout();
                if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
                recordAnswer(false, "");
              }}
            >
              {t("Nie słyszę / Nie rozumiem")} (Esc/N)
            </HearingTestButton>
          )}          <HearingTestButton
            type="button"
            className="btn btn-outline-warning btn-lg"
            iconClassName="bi bi-stop-circle-fill"
            onClick={() => {
              cancelSpeech.current?.();
              cancelSpeech.current = null;
              if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
              onCancel(allWordRecords);
            }}
          >
            {t("Zakończ test")}
          </HearingTestButton>
        </div>
      </div>
    </div>
  );
}
